import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import { Box, Card, CardContent, Typography, Avatar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import RepeatIcon from '@mui/icons-material/Repeat';
import TimelineIcon from '@mui/icons-material/Timeline';
import { mainPageRootStyle } from '../styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadBotActivityByHandle } from './actions';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatNumber } from '../utils/numberUtils';
import { DateRangeFilter } from '../components/DateRangeFilter';
import HandleSelector from '../components/HandleSelector';
import { filterDataByDateRange } from '../utils/dateRangeUtils';
import SkeletonChart from '../components/SkeletonChart';
import SkeletonMetricCards from '../components/SkeletonMetricCards';
import EmptyState from '../components/EmptyState';
import Breadcrumb from '../components/Breadcrumb';

function BotActivityPage() {
    const dispatch = useDispatch();
    const handles = useSelector(state => state.handle.handles);
    const botActivity = useSelector(state => state.botActivity.botActivityByHandle);
    const loading = useSelector(state => state.botActivity.loading);
    const userId = useSelector(state => state.auth.user?.uid);
    const navigate = useNavigate();
    const [dateRange, setDateRange] = useState('3months');

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const selectedHandle = queryParams.get('handle');

    useEffect(() => {
        document.title = 'Bot Activity - Sky Toolbox';
    }, []);

    // Auto-select first handle if none is selected
    useEffect(() => {
        if (!selectedHandle && handles && handles.length > 0) {
            navigate(`/bot-activity?handle=${handles[0].handle}`, { replace: true });
        }
    }, [selectedHandle, handles, navigate]);

    useEffect(() => {
        dispatch(loadBotActivityByHandle(userId, selectedHandle));
    }, [dispatch, selectedHandle, userId]);

    const handleSelectedHandleChange = (event) => {
        navigate(`/bot-activity?handle=${event.target.value}`);
    };

    const handleDateRangeChange = (event) => {
        setDateRange(event.target.value);
    };

    // Group bot activity by day
    const botActivityWithDates = useMemo(() => {
        if (!botActivity || botActivity.length === 0) return [];
        
        // Create a map to group by date
        const activityByDay = new Map();
        
        botActivity.forEach(activity => {
            const timestamp = new Date(activity.timestamp);
            const dateKey = timestamp.toISOString().split('T')[0]; // Get YYYY-MM-DD format
            
            if (!activityByDay.has(dateKey)) {
                activityByDay.set(dateKey, {
                    date: dateKey,
                    likes: 0,
                    follows: 0,
                    unfollows: 0,
                    reposts: 0,
                    timestamp: dateKey
                });
            }
            
            const dayData = activityByDay.get(dateKey);
            dayData.likes += activity.likes || 0;
            dayData.follows += activity.follows || 0;
            dayData.unfollows += activity.unfollows || 0;
            dayData.reposts += activity.reposts || 0;
        });
        
        // Convert to array and sort by date
        const result = Array.from(activityByDay.values());
        result.sort((a, b) => new Date(a.date) - new Date(b.date));
        
        return result.map(item => ({
            ...item,
            date: new Date(item.date)
        }));
    }, [botActivity]);

    const filteredBotActivity = useMemo(() => {
        const filtered = filterDataByDateRange(botActivityWithDates, dateRange);
        
        // Fill in missing days with zero values
        if (filtered.length === 0) return [];
        
        const startDate = new Date(filtered[0].date);
        const endDate = new Date(filtered[filtered.length - 1].date);
        
        // Create a map of existing data for quick lookup
        const dataMap = new Map();
        filtered.forEach(item => {
            const dateKey = item.date.toISOString().split('T')[0];
            dataMap.set(dateKey, item);
        });
        
        // Fill in all days in the range
        const allDays = [];
        const currentDate = new Date(startDate);
        
        while (currentDate <= endDate) {
            const dateKey = currentDate.toISOString().split('T')[0];
            
            if (dataMap.has(dateKey)) {
                allDays.push(dataMap.get(dateKey));
            } else {
                allDays.push({
                    date: new Date(currentDate),
                    likes: 0,
                    follows: 0,
                    unfollows: 0,
                    reposts: 0,
                    timestamp: dateKey
                });
            }
            
            currentDate.setDate(currentDate.getDate() + 1);
        }
        
        return allDays;
    }, [botActivityWithDates, dateRange]);

    const totalActivity = useMemo(() => {
        return filteredBotActivity.reduce((acc, day) => ({
            likes: acc.likes + day.likes,
            follows: acc.follows + day.follows,
            unfollows: acc.unfollows + day.unfollows,
            reposts: acc.reposts + (day.reposts || 0)
        }), { likes: 0, follows: 0, unfollows: 0, reposts: 0 });
    }, [filteredBotActivity]);

    // Group activity by bot type for breakdown
    const activityByBotType = useMemo(() => {
        if (!botActivity || botActivity.length === 0) return {};
        
        const breakdown = {};
        
        botActivity.forEach(activity => {
            const botType = activity.botType || 'Unknown';
            
            if (!breakdown[botType]) {
                breakdown[botType] = {
                    likes: 0,
                    follows: 0,
                    unfollows: 0,
                    reposts: 0
                };
            }
            
            breakdown[botType].likes += activity.likes || 0;
            breakdown[botType].follows += activity.follows || 0;
            breakdown[botType].unfollows += activity.unfollows || 0;
            breakdown[botType].reposts += activity.reposts || 0;
        });
        
        return breakdown;
    }, [botActivity]);

    const isLoading = loading || !selectedHandle;
    const hasData = filteredBotActivity && filteredBotActivity.length > 0;

    // Color palette for bot activity metrics
    const chartColors = {
        'likes': { stroke: '#ef4444', fill: 'url(#colorLikes)' },
        'follows': { stroke: '#10b981', fill: 'url(#colorFollows)' },
        'unfollows': { stroke: '#f59e0b', fill: 'url(#colorUnfollows)' },
        'reposts': { stroke: '#3b82f6', fill: 'url(#colorReposts)' },
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Box
                    sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    }}
                >
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        {new Date(label).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                        })}
                    </Typography>
                    {payload.map((entry, index) => (
                        <Typography 
                            key={index} 
                            variant="body2" 
                            sx={{ color: entry.color }}
                        >
                            {entry.name}: <strong>{formatNumber(entry.value)}</strong>
                        </Typography>
                    ))}
                </Box>
            );
        }
        return null;
    };

    const renderChart = (dataKey, name) => {
        if (filteredBotActivity.length === 0) return null;
        
        const colors = chartColors[dataKey] || { stroke: '#8884d8', fill: 'url(#colorDefault)' };
        const gradientId = colors.fill.match(/url\(#(.+)\)/)?.[1] || 'colorDefault';
        
        return (
            <Card elevation={2} sx={{ mb: 4, p: 2, width: '100%' }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2, ml: 2 }}>
                    {name}
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                        data={filteredBotActivity}
                        margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                    >
                        <defs>
                            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={colors.stroke} stopOpacity={0.8}/>
                                <stop offset="95%" stopColor={colors.stroke} stopOpacity={0.4}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid 
                            strokeDasharray="3 3" 
                            stroke="#e2e8f0"
                            strokeOpacity={0.5}
                        />
                        <XAxis 
                            dataKey="date" 
                            tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric' 
                            })}
                            stroke="#94a3b8"
                            style={{ fontSize: '0.875rem' }}
                        />
                        <YAxis 
                            stroke="#94a3b8"
                            style={{ fontSize: '0.875rem' }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend 
                            wrapperStyle={{ 
                                paddingTop: '20px',
                                fontSize: '0.875rem',
                                fontWeight: 600
                            }}
                        />
                        <Bar 
                            dataKey={dataKey} 
                            name={name} 
                            fill={colors.fill}
                            radius={[8, 8, 0, 0]}
                            animationDuration={1000}
                            animationEasing="ease-in-out"
                        />
                    </BarChart>
                </ResponsiveContainer>
            </Card>
        );
    };

    return (
        <Paper style={mainPageRootStyle} sx={{ p: 3 }}>
            <Breadcrumb items={[
                { label: 'Bot Activity Dashboard' }
            ]} />
            
            {/* Page Header */}
            <Box 
                sx={{ 
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    borderRadius: 3,
                    p: 3,
                    mb: 4,
                    color: 'white',
                }}
            >
                <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 1 }}>
                    🤖 Bot Activity Dashboard
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Monitor your bot automation activities
                </Typography>
            </Box>

            <Box display="flex" justifyContent="center" flexWrap="wrap" gap={2} sx={{ mb: 4 }}>
                <HandleSelector
                    value={selectedHandle}
                    onChange={handleSelectedHandleChange}
                    handles={handles}
                    ariaLabel="Select handle to view bot activity"
                />
                <DateRangeFilter 
                    value={dateRange} 
                    onChange={handleDateRangeChange}
                    sx={{ mt: 0, mb: 0 }}
                />
            </Box>

            {isLoading ? (
                <SkeletonMetricCards count={4} />
            ) : (
                <Box display="flex" justifyContent="center" flexWrap="wrap" gap={3} sx={{ mb: 4 }}>
                    <Card 
                        elevation={0}
                        sx={{ 
                            width: '240px',
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            color: 'white',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(239, 68, 68, 0.4)'
                            }
                        }}
                    >
                        <CardContent>
                            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 48, height: 48, mb: 2 }}>
                                <ThumbUpIcon fontSize="large" />
                            </Avatar>
                            <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                                {formatNumber(totalActivity.likes)}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Total Likes
                            </Typography>
                        </CardContent>
                    </Card>
                    <Card 
                        elevation={0}
                        sx={{ 
                            width: '240px',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            color: 'white',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(59, 130, 246, 0.4)'
                            }
                        }}
                    >
                        <CardContent>
                            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 48, height: 48, mb: 2 }}>
                                <RepeatIcon fontSize="large" />
                            </Avatar>
                            <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                                {formatNumber(totalActivity.reposts)}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Total Reposts
                            </Typography>
                        </CardContent>
                    </Card>
                    <Card 
                        elevation={0}
                        sx={{ 
                            width: '240px',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: 'white',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(16, 185, 129, 0.4)'
                            }
                        }}
                    >
                        <CardContent>
                            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 48, height: 48, mb: 2 }}>
                                <PersonAddIcon fontSize="large" />
                            </Avatar>
                            <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                                {formatNumber(totalActivity.follows)}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Total Follows
                            </Typography>
                        </CardContent>
                    </Card>
                    <Card 
                        elevation={0}
                        sx={{ 
                            width: '240px',
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            color: 'white',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(245, 158, 11, 0.4)'
                            }
                        }}
                    >
                        <CardContent>
                            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 48, height: 48, mb: 2 }}>
                                <PersonRemoveIcon fontSize="large" />
                            </Avatar>
                            <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                                {formatNumber(totalActivity.unfollows)}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Total Unfollows
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
            )}

            {/* Per-Bot Breakdown Section */}
            {!isLoading && hasData && Object.keys(activityByBotType).length > 0 && (
                <Card elevation={2} sx={{ mb: 4, mt: 4 }}>
                    <CardContent>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                            Bot Performance Breakdown
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                            See which bots are generating the most activity
                        </Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Bot Type</strong></TableCell>
                                        <TableCell align="right"><strong>Likes</strong></TableCell>
                                        <TableCell align="right"><strong>Follows</strong></TableCell>
                                        <TableCell align="right"><strong>Reposts</strong></TableCell>
                                        <TableCell align="right"><strong>Unfollows</strong></TableCell>
                                        <TableCell align="right"><strong>Total Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(activityByBotType)
                                        .sort(([, a], [, b]) => {
                                            const totalA = (a.likes || 0) + (a.follows || 0) + (a.reposts || 0) + (a.unfollows || 0);
                                            const totalB = (b.likes || 0) + (b.follows || 0) + (b.reposts || 0) + (b.unfollows || 0);
                                            return totalB - totalA;
                                        })
                                        .map(([botType, stats]) => {
                                            const total = (stats.likes || 0) + (stats.follows || 0) + (stats.reposts || 0) + (stats.unfollows || 0);
                                            return (
                                                <TableRow key={botType} hover>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            {botType === 'TimelineBot' && <Avatar sx={{ width: 32, height: 32, bgcolor: '#ef4444' }}><TimelineIcon fontSize="small" /></Avatar>}
                                                            {botType === 'FeedsBot' && <Avatar sx={{ width: 32, height: 32, bgcolor: '#3b82f6' }}>F</Avatar>}
                                                            {botType === 'SearchBot' && <Avatar sx={{ width: 32, height: 32, bgcolor: '#10b981' }}>S</Avatar>}
                                                            {botType === 'ReposterBot' && <Avatar sx={{ width: 32, height: 32, bgcolor: '#8b5cf6' }}><RepeatIcon fontSize="small" /></Avatar>}
                                                            {botType === 'UnfollowBot' && <Avatar sx={{ width: 32, height: 32, bgcolor: '#f59e0b' }}><PersonRemoveIcon fontSize="small" /></Avatar>}
                                                            {botType === 'Unknown' && <Avatar sx={{ width: 32, height: 32, bgcolor: '#6b7280' }}>?</Avatar>}
                                                            <Typography variant="body2" fontWeight={500}>{botType}</Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="right">{formatNumber(stats.likes || 0)}</TableCell>
                                                    <TableCell align="right">{formatNumber(stats.follows || 0)}</TableCell>
                                                    <TableCell align="right">{formatNumber(stats.reposts || 0)}</TableCell>
                                                    <TableCell align="right">{formatNumber(stats.unfollows || 0)}</TableCell>
                                                    <TableCell align="right"><strong>{formatNumber(total)}</strong></TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {isLoading ? (
                <SkeletonChart count={4} />
            ) : !hasData ? (
                <EmptyState
                    title="No bot activity data available"
                    message="Select a handle and date range to view bot activity metrics"
                />
            ) : (
                <>
                    {renderChart('likes', 'Likes')}
                    {renderChart('reposts', 'Reposts')}
                    {renderChart('follows', 'Follows')}
                    {renderChart('unfollows', 'Unfollows')}
                </>
            )}
        </Paper>
    );
}

export default BotActivityPage;

